import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Pencil } from "lucide-react";
import type { Contact, CustomField } from "@/lib/types";
import type { ApiError } from "@/lib/api";
import { formatCents, formatDate, formatDateTime, mountainInputToISO, mountainInputValue } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useToast } from "@/components/Toaster";
import { useCustomFields, useUpdateContact } from "./hooks";

type Field = { key: keyof Contact; label: string; editable?: boolean; date?: boolean; dateTime?: boolean; placeholder?: string };

const person: Field[] = [
  { key: "prefix", label: "Prefix", editable: true },
  { key: "first_name", label: "First name", editable: true },
  { key: "middle_name", label: "Middle name", editable: true },
  { key: "last_name", label: "Last name", editable: true },
  { key: "suffix", label: "Suffix", editable: true },
  { key: "nickname", label: "Nickname (emails greet with it)", editable: true },
  { key: "company", label: "Company", editable: true },
  { key: "job_title", label: "Job title", editable: true },
  { key: "website", label: "Website", editable: true, placeholder: "https://" },
  { key: "birthday", label: "Birthday", editable: true, date: true, placeholder: "YYYY-MM-DD" },
  { key: "time_zone", label: "Time zone", editable: true, placeholder: "America/Denver" },
  { key: "language", label: "Language", editable: true, placeholder: "en-US" },
];

const attribution: Field[] = [
  { key: "lead_source_raw", label: "Lead source" },
  { key: "first_utm_source", label: "First touch: source" },
  { key: "first_utm_medium", label: "First touch: medium" },
  { key: "first_utm_campaign", label: "First touch: campaign" },
  { key: "last_utm_source", label: "Last touch: source" },
  { key: "last_utm_campaign", label: "Last touch: campaign" },
  { key: "landing_page", label: "Landing page" },
  { key: "referrer", label: "Referrer" },
  { key: "ga4_client_id", label: "GA4 client ID" },
  { key: "gclid", label: "Google click ID" },
  { key: "origin_source", label: "How they were added" },
  { key: "origin_at", label: "Added on", dateTime: true },
  { key: "updated_at", label: "Last changed", dateTime: true },
];

/** Compact details; click a value to edit it in place (Enter saves, Esc cancels). */
export function DetailsGrid({ c }: { c: Contact }) {
  const update = useUpdateContact(c.id);
  const toast = useToast();
  const save = (patch: Record<string, unknown>, label: string) =>
    update.mutate(patch, { onSuccess: () => toast(`${label} saved`), onError: (e) => toast(e.message, "error") });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
        {update.error && <span className="text-xs text-[var(--color-destructive)]">{(update.error as ApiError).message}</span>}
      </CardHeader>
      <CardContent className="space-y-4">
        <Grid>
          {person.map((f) => (
            <TextRow key={f.key} c={c} f={f} onSave={(v) => save({ [f.key]: v }, f.label)} />
          ))}
        </Grid>
        <details className="group border-t pt-3" open={false}>
          <summary className="cursor-pointer select-none text-xs font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
            Where they came from (attribution)
          </summary>
          <Grid className="mt-2">
            {attribution.map((f) => (
              <TextRow key={f.key} c={c} f={f} onSave={() => undefined} />
            ))}
          </Grid>
        </details>
      </CardContent>
    </Card>
  );
}

function Grid({ children, className = "", cols = 2 }: { children: ReactNode; className?: string; cols?: 1 | 2 }) {
  return <div className={`grid grid-cols-1 gap-x-6 gap-y-0.5 ${cols === 2 ? "sm:grid-cols-2" : ""} ${className}`}>{children}</div>;
}

function TextRow({ c, f, onSave }: { c: Contact; f: Field; onSave: (v: string) => void }) {
  const raw = c[f.key] as string | null | undefined;
  const shown = f.date ? formatDate(raw) : f.dateTime ? formatDateTime(raw) : raw || "—";
  return (
    <EditableRow
      label={f.label}
      shown={shown}
      editable={f.editable}
      initial={raw ?? ""}
      onSave={(v) => {
        if ((raw ?? "") !== v) onSave(v);
      }}
      editor={(value, setValue, done, cancel) => (
        <Input
          autoFocus
          className="h-7"
          value={value}
          placeholder={f.placeholder}
          onChange={(e) => setValue(e.target.value)}
          onBlur={done}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === "Enter") done();
            if (e.key === "Escape") cancel();
          }}
          aria-label={f.label}
        />
      )}
    />
  );
}

/** A label + value that turns into an editor on click. */
function EditableRow<T>({
  label,
  shown,
  editable,
  initial,
  onSave,
  editor,
  wide,
}: {
  label: string;
  shown: ReactNode;
  editable?: boolean;
  initial: T;
  onSave: (v: T) => void;
  editor: (value: T, setValue: (v: T) => void, done: () => void, cancel: () => void) => ReactNode;
  wide?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<T>(initial);
  const done = () => {
    setEditing(false);
    onSave(value);
  };
  return (
    <div className={`group grid min-w-0 grid-cols-[8.5rem_minmax(0,1fr)] items-start gap-x-2 py-0.5 ${wide ? "sm:col-span-2" : ""}`}>
      <div className="truncate pt-1 text-xs text-[var(--color-muted-foreground)]" title={label}>
        {label}
      </div>
      {editing ? (
        <div className="min-w-0">{editor(value, setValue, done, () => setEditing(false))}</div>
      ) : (
        <button
          type="button"
          disabled={!editable}
          onClick={() => {
            setValue(initial);
            setEditing(true);
          }}
          className="flex min-h-6 w-full items-center gap-1 rounded text-left text-sm enabled:hover:bg-[var(--color-muted)]"
          title={editable ? "Click to edit" : undefined}
          aria-label={editable ? `Edit ${label}` : undefined}
        >
          <span className="min-w-0 truncate">{shown}</span>
          {editable && <Pencil className="size-3 shrink-0 opacity-0 group-hover:opacity-50" />}
        </button>
      )}
    </div>
  );
}

// ---- custom fields ----

/** Custom fields by group, each with an editor that fits its type. Empty fields are listed too. */
export function CustomFieldsCard({ c }: { c: Contact }) {
  const fields = useCustomFields();
  const update = useUpdateContact(c.id);
  const toast = useToast();
  const list = fields.data?.fields ?? [];
  if (fields.isLoading || list.length === 0) return null;
  const groups = new Map<string, CustomField[]>();
  for (const f of list) {
    const g = f.group_name ?? "Other fields";
    groups.set(g, [...(groups.get(g) ?? []), f]);
  }
  const save = (f: CustomField, v: unknown) =>
    update.mutate({ custom: { [f.key]: v } }, { onSuccess: () => toast(`${f.label} saved`), onError: (e) => toast(e.message, "error") });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom fields</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-x-6 gap-y-4 md:grid-cols-2 2xl:grid-cols-3">
        {[...groups.entries()].map(([g, fs]) => (
          <section key={g} aria-label={g} className="min-w-0">
            <h4 className="mb-1 text-xs font-semibold">{g}</h4>
            <Grid cols={1}>
              {fs.map((f) => (
                <CustomRow key={f.key} f={f} value={c.custom?.[f.key]} onSave={(v) => save(f, v)} />
              ))}
            </Grid>
          </section>
        ))}
      </CardContent>
    </Card>
  );
}

function showCustom(f: CustomField, v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  switch (f.type) {
    case "boolean":
      return v ? "Yes" : "No";
    case "money":
      return formatCents(Number(v));
    case "date":
      return formatDate(String(v));
    case "datetime":
      return formatDateTime(String(v));
    case "multiselect":
      return Array.isArray(v) ? v.join(", ") : String(v);
    default:
      return String(v);
  }
}

export function CustomRow({ f, value, onSave }: { f: CustomField; value: unknown; onSave: (v: unknown) => void }) {
  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const commit = (v: unknown) => {
    if (!same(v, value)) onSave(v === "" ? null : v);
  };
  const text = (type: string, toValue: (s: string) => unknown, fromValue: (v: unknown) => string, hint?: string) => (
    <EditableRow<string>
      label={f.label}
      shown={showCustom(f, value)}
      editable
      initial={fromValue(value)}
      onSave={(s) => {
        // Leaving the field without typing saves nothing, even when the stored form differs (C13).
        if (s.trim() === fromValue(value).trim()) return;
        commit(s.trim() === "" ? null : toValue(s.trim()));
      }}
      editor={(v, set, done, cancel) =>
        f.type === "textarea" ? (
          <Textarea autoFocus className="min-h-16" value={v} onChange={(e) => set(e.target.value)} onBlur={done} onKeyDown={(e) => e.key === "Escape" && cancel()} aria-label={f.label} />
        ) : (
          <Input
            autoFocus
            type={type}
            className="h-7"
            value={v}
            onChange={(e) => set(e.target.value)}
            onBlur={done}
            onKeyDown={(e) => {
              if (e.key === "Enter") done();
              if (e.key === "Escape") cancel();
            }}
            aria-label={hint ? `${f.label} (${hint})` : f.label}
            title={hint}
          />
        )
      }
    />
  );
  switch (f.type) {
    case "number":
      return text("number", (s) => Math.round(Number(s)), (v) => (v == null ? "" : String(v)));
    case "decimal":
      return text("number", (s) => Number(s), (v) => (v == null ? "" : String(v)));
    case "money":
      return text("number", (s) => Math.round(Number(s) * 100), (v) => (v == null ? "" : (Number(v) / 100).toFixed(2)));
    case "date":
      return text("date", (s) => s, (v) => (v == null ? "" : String(v).slice(0, 10)));
    case "datetime":
      // Shown and typed as Mountain Time; stored as the UTC instant (America/Denver explicitly, not the browser's zone).
      return text("datetime-local", (s) => mountainInputToISO(s) || null, (v) => (v == null ? "" : mountainInputValue(String(v))), "Mountain Time");
    case "url":
      return text("url", (s) => s, (v) => (v == null ? "" : String(v)));
    case "email":
      return text("email", (s) => s, (v) => (v == null ? "" : String(v)));
    case "phone":
      return text("tel", (s) => s, (v) => (v == null ? "" : String(v)));
    case "boolean":
    case "select":
      return (
        <EditableRow<string>
          label={f.label}
          shown={showCustom(f, value)}
          editable
          initial={value == null ? "" : String(value)}
          onSave={(s) => commit(f.type === "boolean" ? (s === "" ? null : s === "true") : s || null)}
          editor={(v, set, done) => (
            <Select
              autoFocus
              value={v}
              onChange={(e) => set(e.target.value)}
              onBlur={done}
              onKeyDown={(e) => e.key === "Enter" && done()}
              aria-label={f.label}
            >
              <option value="">—</option>
              {f.type === "boolean" ? (
                <>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </>
              ) : (
                f.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              )}
            </Select>
          )}
        />
      );
    case "multiselect":
      return (
        <EditableRow<string[]>
          label={f.label}
          shown={showCustom(f, value)}
          editable
          initial={Array.isArray(value) ? (value as string[]) : []}
          onSave={(arr) => commit(arr.length ? arr : null)}
          editor={(v, set, done) => (
            <div className="space-y-1 rounded-md border p-2">
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {f.options.map((o) => (
                  <label key={o.value} className="flex items-center gap-1 text-sm">
                    <Checkbox checked={v.includes(o.value)} onChange={(e) => set(e.target.checked ? [...v, o.value] : v.filter((x) => x !== o.value))} />
                    {o.label}
                  </label>
                ))}
              </div>
              <button type="button" className="text-xs text-[var(--color-primary)] hover:underline" onClick={done}>
                Done
              </button>
            </div>
          )}
        />
      );
    default:
      return text("text", (s) => s, (v) => (v == null ? "" : String(v)));
  }
}
